import { Link, useLocation } from 'react-router-dom';
import { AssetName } from '@plerionhq/design-system';
import type { AccessGrant } from '../../types';
import {
  isFederatedPrincipalType,
  mechanismLabels,
  originLabels,
  principalTypeLabels,
  scopeLabels,
} from '../../label-maps';
import { principalToAssetPrn } from '../../lib/principal-to-prn';
import { inventoryFilterLink } from '../../lib/inventory-filter-link';
import {
  AccessGrantDetailRow,
  AccessGrantDetailSection,
} from './access-grant-detail-row';

import { useTranslation } from 'react-i18next';

type AccessGrantOverviewTabProperties = {
  grant: AccessGrant;
};

export function AccessGrantOverviewTab({
  grant,
}: AccessGrantOverviewTabProperties) {
  const { t } = useTranslation('accessGrants');
  const principalPrn = principalToAssetPrn(grant);
  const { search } = useLocation();
  const principalTypeLabel =
    principalTypeLabels(t)[grant.principalType] ?? grant.principalType;

  return (
    <div>
      <AccessGrantDetailSection title={t('grant')} columns={2}>
        <AccessGrantDetailRow
          label={t('mechanism')}
          value={
            <Link
              to={inventoryFilterLink('mechanism', grant.mechanism, search)}
            >
              {mechanismLabels(t)[grant.mechanism]}
            </Link>
          }
        />
        <AccessGrantDetailRow label={t('service')} value={grant.service} />
        <AccessGrantDetailRow
          label={t('scope')}
          value={
            <Link
              to={inventoryFilterLink('grantScope', grant.grantScope, search)}
            >
              {scopeLabels(t)[grant.grantScope]}
            </Link>
          }
        />
        <AccessGrantDetailRow
          label={t('origin')}
          value={
            <Link
              to={inventoryFilterLink('grantOrigin', grant.grantOrigin, search)}
            >
              {originLabels(t)[grant.grantOrigin]}
            </Link>
          }
        />
      </AccessGrantDetailSection>
      <AccessGrantDetailSection title={t('asset')} columns={2}>
        <AccessGrantDetailRow
          label={t('common:name')}
          value={<AssetName id={grant.assetId} name={grant.resourceLabel} />}
          copyValue={grant.assetId}
        />
        <AccessGrantDetailRow
          label={t('type')}
          value={
            <Link
              to={inventoryFilterLink('assetType', grant.assetType, search)}
            >
              {grant.assetType}
            </Link>
          }
        />
        <AccessGrantDetailRow
          label={t('region')}
          value={grant.resourceRegion}
        />
        <AccessGrantDetailRow
          label={t('assetAccount')}
          value={grant.resourceAccountId}
          copyValue={grant.resourceAccountId}
        />
      </AccessGrantDetailSection>
      <AccessGrantDetailSection title={t('principal')} columns={2}>
        <AccessGrantDetailRow
          label={t('principal')}
          value={
            principalPrn ? (
              <AssetName id={principalPrn} name={grant.principal} />
            ) : (
              grant.principal
            )
          }
          copyValue={grant.principal}
        />
        <AccessGrantDetailRow
          label={t('type')}
          value={
            <Link
              to={inventoryFilterLink(
                'principalType',
                grant.principalType,
                search,
              )}
            >
              {principalTypeLabel}
            </Link>
          }
        />
        <AccessGrantDetailRow label={t('label')} value={grant.principalLabel} />
        <AccessGrantDetailRow
          label={t('detail')}
          value={grant.principalDetail}
        />
        {/* Federated grants used to be forced to an em-dash by TYPE, because
            principal_account_id held the IdP-hosting account rather than an
            external principal account. SAML now deliberately keeps that account
            (CS-1538) — it is the trust boundary, and the reason an Identity
            Center grant is internal — so gate on the VALUE instead. OIDC and
            Cognito are still nulled upstream and so still render an em-dash,
            and the row is relabelled for federated grants so the account is not
            misread as "who was granted access". Keeping the type-based check
            here would have shown the account in the grants table (null-based)
            while this panel showed a dash for the same grant. */}
        <AccessGrantDetailRow
          label={
            isFederatedPrincipalType(grant.principalType)
              ? t('identityProviderAccount')
              : t('principalAccount')
          }
          value={grant.principalAccountId ?? '—'}
          copyValue={grant.principalAccountId ?? undefined}
        />
      </AccessGrantDetailSection>
    </div>
  );
}
